import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  AlertTriangle, Radio, Phone, MessageSquare, BarChart3,
  Bell, AlertCircle, Headphones, CheckCircle, Siren,
} from 'lucide-react';
import SectionWrapper from '../../components/marketing/SectionWrapper';
import FeatureCard from '../../components/marketing/FeatureCard';
import CTABanner from '../../components/marketing/CTABanner';

export default function EmergencyPage() {
  const { t } = useTranslation('marketing');

  const features = [
    { icon: Bell, title: t('emergency.feature1_title'), description: t('emergency.feature1_desc'), color: '#dc2626' },
    { icon: Radio, title: t('emergency.feature2_title'), description: t('emergency.feature2_desc'), color: '#0891b2' },
    { icon: Phone, title: t('emergency.feature3_title'), description: t('emergency.feature3_desc'), color: '#7c3aed' },
    { icon: MessageSquare, title: t('emergency.feature4_title'), description: t('emergency.feature4_desc'), color: '#16a34a' },
    { icon: BarChart3, title: t('emergency.feature5_title'), description: t('emergency.feature5_desc'), color: '#0d9488' },
  ];

  const lifecycle = [
    { icon: AlertCircle, label: t('emergency.level1'), desc: t('emergency.level1_desc'), color: '#d97706', bg: '#fef3c7' },
    { icon: AlertTriangle, label: t('emergency.level2'), desc: t('emergency.level2_desc'), color: '#ea580c', bg: '#ffedd5' },
    { icon: Bell, label: t('emergency.level3'), desc: t('emergency.level3_desc'), color: '#dc2626', bg: '#fee2e2' },
    { icon: Headphones, label: t('emergency.inProgress'), desc: t('emergency.inProgress_desc'), color: '#2563eb', bg: '#dbeafe' },
    { icon: Siren, label: t('emergency.emergencyReferral'), desc: t('emergency.emergencyReferral_desc'), color: '#9333ea', bg: '#f3e8ff' },
    { icon: CheckCircle, label: t('emergency.resolved'), desc: t('emergency.resolved_desc'), color: '#16a34a', bg: '#dcfce7' },
  ];

  return (
    <>
      <section className="pt-24 pb-14 bg-gradient-to-br from-red-50/60 via-white to-orange-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-100 text-red-700 text-xs font-semibold tracking-wide uppercase mb-6">
              <AlertTriangle className="w-3.5 h-3.5" />
              {t('emergency.section_title')}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">{t('emergency.title')}</h1>
            <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto">{t('emergency.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <SectionWrapper title={t('emergency.lifecycle_title')} background="white">
        <div className="max-w-2xl mx-auto">
          {lifecycle.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="flex gap-3 items-start">
                <div className="flex flex-col items-center">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: step.bg }}
                  >
                    <step.icon className="w-5 h-5" style={{ color: step.color }} />
                  </div>
                  {i < lifecycle.length - 1 && (
                    <div className="w-px h-6 bg-gray-200 my-1" />
                  )}
                </div>
                <div className="pb-4">
                  <h3 className="font-bold text-gray-900 text-[15px]">{step.label}</h3>
                  <p className="text-gray-500 text-[13px] mt-0.5">{step.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </SectionWrapper>

      <SectionWrapper background="gray">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {features.map((f, i) => (
            <FeatureCard key={i} icon={f.icon} title={f.title} description={f.description} color={f.color} index={i} />
          ))}
        </div>
      </SectionWrapper>

      <CTABanner
        title={t('cta.title')}
        subtitle={t('cta.subtitle')}
        buttonText={t('cta.button')}
      />
    </>
  );
}
